/**
 * Habit streak engine — pure functions computing the current/longest streak
 * and the visual state (🔥 hot / 🧊 frozen / grey cold) for a habit.
 *
 * Rules (see .claude/tasks/habits-feature.md, D5/D5b/D11):
 * - Streak = consecutive completed *due* occurrences, walked back from today.
 * - `graceDays` tolerates up to N consecutive missed due occurrences (they
 *   pause the streak — neither increment nor break — and put it in 'frozen'),
 *   the (N+1)-th consecutive miss breaks it to 0.
 * - "Today" still open does not break (the day is still running) → 'cold'.
 * - Fixed-day frequencies walk by due DAY; `timesPerWeek` walks by Mo–So WEEK
 *   (quota met = week counts); grace then applies in weeks.
 */
import type { Habit } from '../db/models'
import { addDays, dateKey, daysBetween, startOfDay, startOfWeek } from '../date/calendar'
import { isDueOn, weekCompletionCount } from './recurrence'

export type StreakState = 'hot' | 'frozen' | 'cold'

export interface StreakResult {
  current: number
  longest: number
  state: StreakState
}

// Safety cap on how far back we scan (defensive against a far-past startDate).
const MAX_LOOKBACK_DAYS = 2200 // ~6 years
const MAX_LOOKBACK_WEEKS = 320 // ~6 years

const EMPTY: StreakResult = { current: 0, longest: 0, state: 'cold' }

/**
 * @param completedKeys set of 'YYYY-MM-DD' keys where the habit was completed
 * @param today the reference "today" (local)
 * @param graceDays global streak grace (consecutive misses tolerated)
 */
export function computeStreak(
  habit: Pick<Habit, 'frequency' | 'startDate'>,
  completedKeys: Set<string>,
  today: Date,
  graceDays: number,
): StreakResult {
  const grace = Math.max(0, graceDays)
  if (habit.frequency.kind === 'timesPerWeek') {
    return computeWeeklyStreak(habit.frequency.count, habit.startDate, completedKeys, today, grace)
  }
  return computeDailyStreak(habit, completedKeys, today, grace)
}

function computeDailyStreak(
  habit: Pick<Habit, 'frequency' | 'startDate'>,
  completedKeys: Set<string>,
  today: Date,
  grace: number,
): StreakResult {
  const { frequency, startDate } = habit
  const start = startOfDay(startDate)
  const todayD = startOfDay(today)
  if (daysBetween(start, todayD) < 0) return EMPTY // not started yet

  const due = (d: Date) => isDueOn(frequency, startDate, d)
  const done = (d: Date) => completedKeys.has(dateKey(d))

  const todayDue = due(todayD)
  const todayDone = todayDue && done(todayD)

  // ---- current streak: walk backwards from today ----
  let current = 0
  let missRun = 0
  let leadingMiss = 0
  let sawDone = false

  // Skip a still-open today so it does not count as a miss.
  let cursor = todayDue && !todayDone ? addDays(todayD, -1) : todayD
  let guard = 0
  while (daysBetween(start, cursor) >= 0 && guard++ < MAX_LOOKBACK_DAYS) {
    if (due(cursor)) {
      if (done(cursor)) {
        current++
        missRun = 0
        sawDone = true
      } else {
        missRun++
        if (!sawDone) leadingMiss = missRun
        if (missRun > grace) break
      }
    }
    cursor = addDays(cursor, -1)
  }

  // ---- longest streak: scan forward applying the same tolerance ----
  const longest = longestDaily(frequency, startDate, completedKeys, start, todayD, todayDue, todayDone, grace)

  const state = streakState({ current, leadingMiss, todayDue, todayDone })
  return { current, longest, state }
}

function longestDaily(
  frequency: Habit['frequency'],
  startDate: Date,
  completedKeys: Set<string>,
  start: Date,
  todayD: Date,
  todayDue: boolean,
  todayDone: boolean,
  grace: number,
): number {
  let longest = 0
  let run = 0
  let missRun = 0
  let cursor = start
  let guard = 0
  while (daysBetween(cursor, todayD) >= 0 && guard++ < MAX_LOOKBACK_DAYS) {
    if (isDueOn(frequency, startDate, cursor)) {
      const isToday = daysBetween(cursor, todayD) === 0
      if (isToday && todayDue && !todayDone) {
        // pending today — neither counts nor breaks
      } else if (completedKeys.has(dateKey(cursor))) {
        run++
        missRun = 0
        if (run > longest) longest = run
      } else {
        missRun++
        if (missRun > grace) {
          run = 0
          missRun = 0
        }
      }
    }
    cursor = addDays(cursor, 1)
  }
  return longest
}

function computeWeeklyStreak(
  count: number,
  startDate: Date,
  completedKeys: Set<string>,
  today: Date,
  grace: number,
): StreakResult {
  if (count < 1) return EMPTY
  const startWeek = startOfWeek(startDate)
  const thisWeek = startOfWeek(today)
  if (daysBetween(startWeek, thisWeek) < 0) return EMPTY

  const met = (weekMonday: Date) => weekCompletionCount(completedKeys, weekMonday) >= count
  const thisWeekMet = met(thisWeek)

  // ---- current: walk back over weeks ----
  let current = 0
  let missRun = 0
  let leadingMiss = 0
  let sawDone = false

  let cursor = thisWeekMet ? thisWeek : addDays(thisWeek, -7) // skip pending current week
  let guard = 0
  while (daysBetween(startWeek, cursor) >= 0 && guard++ < MAX_LOOKBACK_WEEKS) {
    if (met(cursor)) {
      current++
      missRun = 0
      sawDone = true
    } else {
      missRun++
      if (!sawDone) leadingMiss = missRun
      if (missRun > grace) break
    }
    cursor = addDays(cursor, -7)
  }

  // ---- longest: scan forward over weeks ----
  let longest = 0
  let run = 0
  let mr = 0
  let fwd = startWeek
  guard = 0
  while (daysBetween(fwd, thisWeek) >= 0 && guard++ < MAX_LOOKBACK_WEEKS) {
    const isThisWeek = daysBetween(fwd, thisWeek) === 0
    if (isThisWeek && !thisWeekMet) {
      // pending current week — skip
    } else if (met(fwd)) {
      run++
      mr = 0
      if (run > longest) longest = run
    } else {
      mr++
      if (mr > grace) {
        run = 0
        mr = 0
      }
    }
    fwd = addDays(fwd, 7)
  }

  // For weekly habits the current week is always "due".
  const state = streakState({ current, leadingMiss, todayDue: true, todayDone: thisWeekMet })
  return { current, longest, state }
}

function streakState(args: {
  current: number
  leadingMiss: number
  todayDue: boolean
  todayDone: boolean
}): StreakState {
  const { current, leadingMiss, todayDue, todayDone } = args
  if (todayDone) return 'hot'
  if (current > 0 && leadingMiss > 0) return 'frozen'
  if (todayDue && !todayDone && current > 0) return 'cold'
  if (current > 0) return 'hot'
  return 'cold'
}
